import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { TextFieldModule } from '@angular/cdk/text-field';
import { ConfirmInvoiceDialogComponent } from '@app/confirm-invoice-dialog/confirm-invoice-dialog.component';
import { DownloadConfirmationDialogComponent } from '@app/download-confirmation-dialog/download-confirmation-dialog.component';
import { ConfirmEmailSendDialogComponent } from '@app/confirm-email-send-dialog/confirm-email-send-dialog.component';
import { LoadingDialogComponent } from '@app/loading-dialog/loading-dialog.component';
import { PdfViewerDialogComponent } from '@app/pdf-viewer-dialog/pdf-viewer-dialog.component';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  FormControl,
} from '@angular/forms';
import { forkJoin, Observable, of } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { Router } from '@angular/router';

import { Organizations } from '@app/interface/organizations';
import {
  InvoiceInterface,
  PensionFundCode,
  PaymentMethodCode,
} from '@app/interface/invoice';
import { CompanyInterface } from '@app/interface/company';
import { ProductServiceInterface } from '@app/interface/product-service';
import {
  TaxWithholdingInterface,
  KindCode,
} from '@app/interface/tax-withholding';
import { SavedProductInterface } from '@app/interface/savedproduct';
import { AtecoCodeInterface } from '@app/interface/ateco-code';

import { InvoiceApi } from '@app/services/invoice-api';
import { CompanyApi } from '@app/services/company-api';
import { ProductserviceApiService } from '@app/services/productservice-api.service';
import { TaxWithholdingApi } from '@app/services/tax-withholding-api.service';
import { SavedProductsApi } from '@app/services/savedproducts-api.service';
import { AtecoCodeApiService } from '@app/services/ateco-code-api.service';
import { OrganizationApi } from '@app/services/organization-api.service';
import {
  QuickInvoiceDraft,
  QuickInvoiceTransferService,
} from '@app/services/quick-invoice-transfer.service';

export interface QuickInvoiceData {
  organizationId: number;
  organization: Organizations;
}

// Voce già aggiunta al documento (mostrata come card, in stile new-invoice)
interface QuickProductLine {
  name: string;
  quantity: number;
  units: string;
  gross_amount: number;
  discount: number;
  description: string;
  lineTotal: number;
}

// Voce in fase di compilazione, prima di essere aggiunta
interface NewQuickProductLine {
  name: string;
  quantity: number;
  units: string;
  gross_amount: number;
  discount: number;
  description: string;
  lineTotal: number;
}

@Component({
  selector: 'izy-quick-invoice-bottom-sheet',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatTooltipModule,
    MatExpansionModule,
    TextFieldModule,
  ],
  templateUrl: './quick-invoice-bottom-sheet.component.html',
  styleUrl: './quick-invoice-bottom-sheet.component.scss',
})
export class QuickInvoiceBottomSheetComponent implements OnInit {
  // ─── Loading / error ──────────────────────────────────────────────────────
  isLoading = false;
  isSubmitting = false;
  isGeneratingPreview = false;
  errorMessage: string | null = null;

  // ─── Data loaded at init ───────────────────────────────────────────────────
  progressiveNumber: number = 1;
  enumeration: string = String(new Date().getFullYear());
  companies: CompanyInterface[] = [];
  savedProducts: SavedProductInterface[] = [];
  atecoCodes: AtecoCodeInterface[] = [];
  selectedAtecoCodeId: number | null = null;
  filteredCompanies$!: Observable<CompanyInterface[]>;

  // ─── Fatturazione: metodo di pagamento, data di emissione, termini, SDI ────
  paymentMethod: PaymentMethodCode = 'MP05';
  emissionDate: string = new Date().toISOString().split('T')[0];
  paymentDays: number = 0;
  addresseeCode: string = '0000000';
  iban: string = '';

  // IBAN validation
  isValidIban: boolean = true;
  ibanPattern: RegExp = /^[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{11,30}$/;

  validateIban(): void {
    if (this.iban) {
      this.isValidIban = this.ibanPattern.test(this.iban);
    } else {
      this.isValidIban = true;
    }
  }

  readonly paymentMethodOptions: { code: PaymentMethodCode; name: string }[] = [
    { code: 'MP01', name: 'Contanti' },
    { code: 'MP02', name: 'Assegno' },
    { code: 'MP03', name: 'Assegno circolare' },
    { code: 'MP04', name: 'Contanti presso Tesoreria' },
    { code: 'MP05', name: 'Bonifico' },
    { code: 'MP06', name: 'Vaglia cambiario' },
    { code: 'MP07', name: 'Bollettino bancario' },
    { code: 'MP08', name: 'Carta di credito' },
    { code: 'MP09', name: 'RID' },
    { code: 'MP10', name: 'RID utenze' },
    { code: 'MP11', name: 'RID veloce' },
    { code: 'MP12', name: 'Riba' },
    { code: 'MP13', name: 'MAV' },
    { code: 'MP14', name: 'Quietanza erario stato' },
    { code: 'MP15', name: 'Giroconto su conti di contabilità speciale' },
    { code: 'MP16', name: 'Domiciliazione bancaria' },
    { code: 'MP17', name: 'Domiciliazione postale' },
  ];

  // ─── Main form ─────────────────────────────────────────────────────────────
  mainForm!: FormGroup;
  companySearchControl = new FormControl('');
  selectedCompany: CompanyInterface | null = null;

  // ─── Product lines ─────────────────────────────────────────────────────────
  productLines: QuickProductLine[] = [];

  // Form di inserimento nuova voce (autocomplete + campi), stile new-invoice mobile
  newProductNameControl = new FormControl('');
  filteredNewProducts$!: Observable<SavedProductInterface[]>;
  newProductLine: NewQuickProductLine = this.emptyNewLine();

  // ─── Contributi (withholdings) ─────────────────────────────────────────────
  hasRivalsa = false;
  rivalsaRate = 4;
  rivalsaTotalPercentage = 100;

  hasCassa = false;
  cassaRate = 4;
  cassaTotalPercentage = 100;
  cassaPensionFundType: PensionFundCode = 'TC22';

  hasRitenutaAcconto = false;
  ritenutaAccontoRate = 20;
  ritenutaAccontoTotalPercentage = 100;

  hasRitenuta = false;
  ritenutaRate = 20;
  ritenutaTotalPercentage = 100;

  // ─── Riepilogo importi (footer) ─────────────────────────────────────────────
  showTotalsDetails = false;

  // ─── Marca da bollo ─────────────────────────────────────────────────────────
  revenueStampOption: 'none' | 'customer' | 'me' = 'none';
  invoiceRevenueStamp = 0;
  invoiceRevenueStampOnUser = false;

  // ─── Calculated totals (mirrano new-invoice.calculateAllTaxWithholdings) ───
  grossAmount = 0; // totale prestazioni, prima dello sconto
  discountAmount = 0; // sconto totale sulle prestazioni
  totalBase = 0; // imponibile prestazioni (gross - sconto)
  rivalsaAmount = 0;
  totalWithRivalsa = 0; // competenze + rivalsa
  cassaAmount = 0;
  totalTaxable = 0; // imponibile dopo rivalsa + cassa
  ritenutaAccontoAmount = 0;
  ritenutaAmount = 0;
  ivaAmount = 0;
  grandTotal = 0;

  // ─── Pension fund options ──────────────────────────────────────────────────
  readonly pensionFundOptions = [
    { code: 'TC01', name: 'Avvocati (CNPA)' },
    { code: 'TC02', name: 'Dottori commercialisti' },
    { code: 'TC03', name: 'Geometri' },
    { code: 'TC04', name: 'Ingegneri e architetti' },
    { code: 'TC05', name: 'Notariato' },
    { code: 'TC06', name: 'Ragionieri e periti' },
    { code: 'TC07', name: 'ENASARCO' },
    { code: 'TC08', name: 'Consulenti del lavoro (ENPACL)' },
    { code: 'TC09', name: 'Medici (ENPAM)' },
    { code: 'TC10', name: 'Farmacisti (ENPAF)' },
    { code: 'TC11', name: 'Veterinari (ENPAV)' },
    { code: 'TC12', name: 'Impiegati agricoltura (ENPAIA)' },
    { code: 'TC13', name: 'Spedizioni e agenzie marittime' },
    { code: 'TC14', name: 'Giornalisti (INPGI)' },
    { code: 'TC15', name: 'Sanitari orfani (ONAOSI)' },
    { code: 'TC16', name: 'Giornalisti integrativi (CASAGIT)' },
    { code: 'TC17', name: 'Periti industriali (EPPI)' },
    { code: 'TC18', name: 'Pluricategoriale (EPAP)' },
    { code: 'TC19', name: 'Biologi (ENPAB)' },
    { code: 'TC20', name: 'Infermieri (ENPAPI)' },
    { code: 'TC21', name: 'Psicologi (ENPAP)' },
    { code: 'TC22', name: 'INPS' },
  ];

  constructor(
    private bottomSheetRef: MatBottomSheetRef<QuickInvoiceBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: QuickInvoiceData,
    private fb: FormBuilder,
    private invoiceApi: InvoiceApi,
    private companyApi: CompanyApi,
    private productServiceApi: ProductserviceApiService,
    private taxWithholdingApi: TaxWithholdingApi,
    private savedProductsApi: SavedProductsApi,
    private atecoCodeApi: AtecoCodeApiService,
    private organizationApi: OrganizationApi,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private router: Router,
    private quickInvoiceTransferService: QuickInvoiceTransferService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.initDefaults();
    this.loadSettings();
    this.loadInitialData();
  }

  // ─── Initialisation ─────────────────────────────────────────────────────────

  private buildForm(): void {
    this.mainForm = this.fb.group({
      description: ['', Validators.required],
    });
  }

  private initDefaults(): void {
    // Valori di partenza presi dall'organization già caricata dal componente
    // chiamante, in attesa che loadSettings() li aggiorni con i dati freschi.
    const org = this.data.organization;
    this.cassaPensionFundType = org?.favorite_pension_fund ?? 'TC22';
  }

  // Ricarica le impostazioni dell'organization dal server (stesso comportamento
  // di new-invoice.loadSettings), in modo da avere sempre i valori più recenti
  // anche se la organization passata nel data del bottom sheet è stata caricata
  // tempo prima nel componente chiamante.
  private loadSettings(): void {
    const orgId = this.data.organizationId;
    if (!orgId) return;

    this.organizationApi.getOrganization(orgId, {}).subscribe({
      next: (organization: Organizations) => {
        this.isHealthcareInvoice = !!organization.is_healthcare;

        if (organization.has_revenue_stamp_for_company) {
          this.onRevenueStampOptionChange('customer');
        } else {
          this.onRevenueStampOptionChange('none');
        }

        this.cassaPensionFundType =
          organization.favorite_pension_fund ?? 'TC22';
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading organization settings:', err);
        // In caso di errore manteniamo i valori di default già impostati
      },
    });
  }

  private loadInitialData(): void {
    this.isLoading = true;
    const orgId = this.data.organizationId;

    // Progressive number
    this.invoiceApi.getMaxProgressiveNumber(orgId).subscribe({
      next: (max) => {
        this.progressiveNumber = (max ?? 0) + 1;
        this.cdr.detectChanges();
      },
    });

    // Companies for autocomplete
    this.companyApi.getAllCompanies<CompanyInterface>(orgId, {}).subscribe({
      next: (res) => {
        if (res.completed) {
          this.companies = res.results;
          this.setupCompanyFilter();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });

    // Saved products for autocomplete
    this.savedProductsApi
      .getAllSavedProducts<SavedProductInterface>(orgId, { is_deleted: false })
      .subscribe({
        next: (res) => {
          if (res.completed) {
            this.savedProducts = res.results;
            this.cdr.detectChanges();
          }
        },
      });

    // ATECO codes
    this.atecoCodeApi
      .getAllAtecoCodes<AtecoCodeInterface>(orgId, {})
      .subscribe({
        next: (res) => {
          if (res.completed) {
            this.atecoCodes = res.results;
            // Pre-seleziona il primo codice disponibile, come in new-invoice
            if (!this.selectedAtecoCodeId && this.atecoCodes.length > 0) {
              this.selectedAtecoCodeId = this.atecoCodes[0].id;
            }
            this.cdr.detectChanges();
          }
        },
      });

    this.setupNewProductFilter();
  }

  private setupCompanyFilter(): void {
    this.filteredCompanies$ = this.companySearchControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const search = (typeof value === 'string' ? value : '').toLowerCase();
        return this.companies.filter((c) =>
          c.company_name.toLowerCase().includes(search),
        );
      }),
    );
  }

  // ─── Company autocomplete ────────────────────────────────────────────────────

  displayCompany(company: CompanyInterface | null): string {
    return company?.company_name ?? '';
  }

  onCompanySelected(company: CompanyInterface): void {
    this.selectedCompany = company;
    // Codice destinatario (SDI): caricato in automatico dall'anagrafica
    // cliente, come in new-invoice. Resta comunque modificabile a mano.
    this.addresseeCode = company.addressee_code || '0000000';
  }

  clearCompany(): void {
    this.selectedCompany = null;
    this.companySearchControl.setValue('');
    this.addresseeCode = '0000000';
  }

  // ─── Fattura cartacea / prestazione sanitaria ───────────────────────────────
  // Una fattura per prestazione sanitaria non viene inoltrata allo SDI: è quindi
  // sempre (anche) una fattura cartacea, esattamente come in new-invoice.
  isPaperInvoice = false;
  isHealthcareInvoice = false;

  onPaperInvoiceToggleChange(): void {
    this.cdr.detectChanges();
  }

  onHealthcareToggleChange(): void {
    this.cdr.detectChanges();
  }

  // ─── Product lines (stile new-invoice mobile: card per le voci già aggiunte
  // + form separato per inserirne una nuova) ──────────────────────────────────

  private emptyNewLine(): NewQuickProductLine {
    return {
      name: '',
      quantity: 1,
      units: '',
      gross_amount: 0,
      discount: 0,
      description: '',
      lineTotal: 0,
    };
  }

  private setupNewProductFilter(): void {
    this.filteredNewProducts$ = this.newProductNameControl.valueChanges.pipe(
      startWith(''),
      debounceTime(150),
      map((value) => {
        const search = (typeof value === 'string' ? value : '').toLowerCase();
        return this.savedProducts.filter((p) =>
          p.name.toLowerCase().includes(search),
        );
      }),
    );
  }

  onSavedProductSelected(product: SavedProductInterface): void {
    // the name should be editable
    this.newProductNameControl.setValue('', { emitEvent: false });
    this.newProductLine.name = product.name;
    this.newProductLine.gross_amount = product.unit_price;
    this.newProductLine.units = product.units;
    this.newProductLine.description = product.description;
    this.newProductLine.quantity = 1;
    this.newProductLine.discount = 0;
    this.calculateNewLineTotal();
  }

  displayProduct(name: string | null): string {
    return name ?? '';
  }

  calculateNewLineTotal(): void {
    const base =
      this.newProductLine.gross_amount * this.newProductLine.quantity;
    const discountAmount = base * (this.newProductLine.discount / 100);
    this.newProductLine.lineTotal = this.roundToTwo(base - discountAmount);
  }

  get isNewLineValid(): boolean {
    return (
      !!this.newProductLine.name.trim() &&
      this.newProductLine.gross_amount > 0 &&
      this.newProductLine.quantity > 0
    );
  }

  addProductLine(): void {
    if (!this.isNewLineValid) return;

    this.newProductLine.name = this.newProductLine.name.trim();
    this.calculateNewLineTotal();

    this.productLines.push({ ...this.newProductLine });

    // Reset del form di inserimento
    this.newProductLine = this.emptyNewLine();
    this.newProductNameControl.setValue('');

    this.recalculate();
  }

  removeProductLine(index: number): void {
    this.productLines.splice(index, 1);
    this.recalculate();
  }

  // ─── Contributi toggles ─────────────────────────────────────────────────────
  // Nota: il valore booleano (hasRivalsa, hasCassa, ...) viene già aggiornato
  // dal binding [(ngModel)] sul mat-slide-toggle: questi handler si occupano
  // solo di ricalcolare i totali, senza negare di nuovo il valore (altrimenti
  // il toggle non avrebbe alcun effetto visibile sulla sezione collegata).

  toggleRivalsa(): void {
    this.recalculate();
  }

  toggleCassa(): void {
    this.recalculate();
  }

  toggleRitenutaAcconto(): void {
    this.recalculate();
  }

  toggleRitenuta(): void {
    this.recalculate();
  }

  onRateChange(): void {
    this.recalculate();
  }

  toggleTotalsDetails(): void {
    this.showTotalsDetails = !this.showTotalsDetails;
  }

  get numeroContributi(): number {
    return (
      (this.hasRivalsa ? 1 : 0) +
      (this.hasCassa ? 1 : 0) +
      (this.hasRitenutaAcconto ? 1 : 0) +
      (this.hasRitenuta ? 1 : 0)
    );
  }

  // ─── Marca da bollo ─────────────────────────────────────────────────────────

  onRevenueStampOptionChange(option: 'none' | 'customer' | 'me'): void {
    this.revenueStampOption = option;

    if (option === 'customer') {
      this.invoiceRevenueStamp = 2;
      this.invoiceRevenueStampOnUser = false;
    } else if (option === 'me') {
      this.invoiceRevenueStamp = 0;
      this.invoiceRevenueStampOnUser = true;
    } else {
      this.invoiceRevenueStamp = 0;
      this.invoiceRevenueStampOnUser = false;
    }

    this.recalculate();
  }

  onRevenueStampCustomerToggle(checked: boolean): void {
    this.onRevenueStampOptionChange(checked ? 'customer' : 'none');
  }

  onRevenueStampMeToggle(checked: boolean): void {
    this.onRevenueStampOptionChange(checked ? 'me' : 'none');
  }

  // ─── Calculation logic (mirrors new-invoice calculateAllTaxWithholdings) ───

  recalculate(): void {
    // Totale prestazioni prima dello sconto
    this.grossAmount = this.roundToTwo(
      this.productLines.reduce(
        (sum, l) => sum + l.gross_amount * l.quantity,
        0,
      ),
    );

    // Imponibile prestazioni dopo sconto (no IVA per forfettario)
    this.totalBase = this.roundToTwo(
      this.productLines.reduce((sum, l) => sum + l.lineTotal, 0),
    );

    this.discountAmount = this.roundToTwo(this.grossAmount - this.totalBase);

    // Step 0: marca da bollo a carico del cliente, prima della rivalsa
    let currentAmount = this.totalBase;
    if (this.revenueStampOption === 'customer') {
      currentAmount += this.invoiceRevenueStamp;
    }

    // Step 1: RIVALSA su competenze (+ bollo se a carico cliente),
    // calcolata solo sulla quota "Su imponibile (%)" impostata dall'utente
    this.rivalsaAmount = this.hasRivalsa
      ? this.roundToTwo(
          (currentAmount *
            (this.rivalsaTotalPercentage / 100) *
            this.rivalsaRate) /
            100,
        )
      : 0;

    this.totalWithRivalsa = this.roundToTwo(currentAmount + this.rivalsaAmount);

    // Step 2: CASSA su competenze + rivalsa (sulla quota imponibile selezionata)
    this.cassaAmount = this.hasCassa
      ? this.roundToTwo(
          (this.totalWithRivalsa *
            (this.cassaTotalPercentage / 100) *
            this.cassaRate) /
            100,
        )
      : 0;

    this.totalTaxable = this.roundToTwo(
      this.totalWithRivalsa + this.cassaAmount,
    );

    // Step 3: IVA = 0% (forfettario)
    this.ivaAmount = 0;
    const totalWithIva = this.totalTaxable + this.ivaAmount;

    // Step 4: RITENUTA_ACCONTO su competenze + rivalsa (non su cassa)
    this.ritenutaAccontoAmount = this.hasRitenutaAcconto
      ? this.roundToTwo(
          (this.totalWithRivalsa *
            (this.ritenutaAccontoTotalPercentage / 100) *
            this.ritenutaAccontoRate) /
            100,
        )
      : 0;

    // Step 5: RITENUTA su competenze
    this.ritenutaAmount = this.hasRitenuta
      ? this.roundToTwo(
          (this.totalBase *
            (this.ritenutaTotalPercentage / 100) *
            this.ritenutaRate) /
            100,
        )
      : 0;

    this.grandTotal = this.roundToTwo(
      totalWithIva - this.ritenutaAccontoAmount - this.ritenutaAmount,
    );

    this.cdr.detectChanges();
  }

  private roundToTwo(n: number): number {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  // Data di emissione + termini di pagamento (giorni), stessa logica di
  // new-invoice.calculatePaymentDate.
  private calculatePaymentDate(emissionDate: string, days: number): string {
    const date = emissionDate ? new Date(emissionDate) : new Date();
    date.setDate(date.getDate() + (days || 0));
    return date.toISOString().split('T')[0];
  }

  // ─── Validation ─────────────────────────────────────────────────────────────

  get isFormValid(): boolean {
    if (!this.selectedCompany) return false;
    if (!this.mainForm.valid) return false;
    if (!this.progressiveNumber || this.progressiveNumber <= 0) return false;
    if (!this.enumeration || !this.enumeration.trim()) return false;
    if (!this.isAddresseeCodeValid) return false;
    if (!this.isValidIban) return false;
    if (this.productLines.length === 0) return false;
    if (this.productLines.some((l) => !l.name || l.gross_amount <= 0))
      return false;
    return true;
  }

  // Stesso controllo applicato a new-company.addressee_code: 6 o 7
  // caratteri alfanumerici maiuscoli.
  get isAddresseeCodeValid(): boolean {
    return /^[A-Z0-9]{6,7}$/.test(this.addresseeCode || '');
  }

  // Spiega all'utente perché il pulsante "Invia" è disattivato (usato come
  // matTooltip), elencando solo ciò che manca davvero.
  get formValidationMessage(): string {
    const missing: string[] = [];
    if (!this.selectedCompany) missing.push('seleziona un cliente');
    if (!this.progressiveNumber || this.progressiveNumber <= 0)
      missing.push('inserisci il numero progressivo');
    if (!this.enumeration || !this.enumeration.trim())
      missing.push('inserisci la numerazione');
    if (this.mainForm.get('description')?.invalid)
      missing.push('inserisci la causale');
    if (!this.isAddresseeCodeValid)
      missing.push(
        'correggi il codice destinatario (6 o 7 caratteri alfanumerici maiuscoli)',
      );
    if (this.productLines.length === 0)
      missing.push('aggiungi almeno una prestazione');
    else if (this.productLines.some((l) => !l.name || l.gross_amount <= 0))
      missing.push('completa le prestazioni inserite');
    if (!this.isValidIban) missing.push("correggi l'IBAN");

    if (missing.length === 0) return '';
    return 'Per inviare la fattura: ' + missing.join(', ') + '.';
  }

  // ─── Submit ─────────────────────────────────────────────────────────────────

  async submit(onlyForPreview: boolean = false): Promise<void> {
    if (!this.isFormValid || this.isSubmitting || this.isGeneratingPreview)
      return;

    // In modalità anteprima saltiamo il dialog di conferma invio (stesso
    // comportamento di new-invoice.addNewElement)
    if (!onlyForPreview) {
      const dialogRef = this.dialog.open(ConfirmInvoiceDialogComponent, {
        width: '90vw',
        maxWidth: '400px',
        height: '400px',
        data: {
          organization_source: this.data.organization?.source,
        },
      });
      const result = await dialogRef.afterClosed().toPromise();

      if (!result) {
        // L'utente ha annullato il dialog
        return;
      }
    }

    if (onlyForPreview) {
      this.isGeneratingPreview = true;
    } else {
      this.isSubmitting = true;
    }

    const orgId = this.data.organizationId;
    const org = this.data.organization;

    const invoicePayload: Partial<InvoiceInterface> = {
      organization_id: orgId,
      company_id: this.selectedCompany!.id,
      addressee_code: this.addresseeCode || '0000000',
      iban: this.iban || undefined,
      document_type: 'TD01',
      emission_date: this.emissionDate,
      payment_method: this.paymentMethod,
      payment_days: this.paymentDays,
      payment_date: this.calculatePaymentDate(
        this.emissionDate,
        this.paymentDays,
      ),
      currency: 'EUR',
      language: 'it',
      is_draft: onlyForPreview,
      is_deleted: false,
      is_preventive: false,
      is_saved_for_preview: onlyForPreview,
      invoice_name: onlyForPreview ? '(Anteprima)' : '',
      progressive_number: this.progressiveNumber,
      enumeration: this.enumeration,
      description: this.mainForm.value.description,
      iva_percentage: 0,
      iva_collectability: 'IMMEDIATA',
      paper_invoice: this.isPaperInvoice,
      healthcare_invoice: this.isHealthcareInvoice,
      // Amounts (mirrano la mappatura di new-invoice.addNewElement)
      gross_amount:
        this.revenueStampOption === 'customer'
          ? this.grossAmount + this.invoiceRevenueStamp
          : this.grossAmount,
      discount_amount: this.discountAmount,
      amount: this.grandTotal,
      iva_amount: this.ivaAmount,
      revenue_stamp:
        this.revenueStampOption === 'me' ? 2 : this.invoiceRevenueStamp,
      revenue_stamp_for_company: this.revenueStampOption === 'customer',
      revenge: this.rivalsaAmount,
      revenge_rate: this.hasRivalsa ? this.rivalsaRate : 0,
      pension_fund: this.cassaAmount,
      pension_fund_rate: this.hasCassa ? this.cassaRate : 0,
      pension_fund_type: this.hasCassa
        ? this.cassaPensionFundType
        : (org?.favorite_pension_fund ?? 'TC22'),
      taxable_amount: this.totalTaxable,
      withholding_advance_tax: this.ritenutaAccontoAmount,
      withholding_advance_tax_rate: this.hasRitenutaAcconto
        ? this.ritenutaAccontoRate
        : 0,
      withholding_tax: this.ritenutaAmount,
      withholding_tax_rate: this.hasRitenuta ? this.ritenutaRate : 0,
      // ATECO
      ateco_code_id:
        this.selectedAtecoCodeId ?? this.atecoCodes[0]?.id ?? undefined,
    };

    this.invoiceApi.createInvoice(orgId, invoicePayload).subscribe({
      next: (invoice: InvoiceInterface) => {
        this.saveProductsAndWithholdings(invoice, onlyForPreview);
      },
      error: (err) => {
        console.error('Error creating invoice:', err);
        this.isSubmitting = false;
        this.isGeneratingPreview = false;
        this.showError('Errore durante la creazione della fattura.');
        this.cdr.detectChanges();
      },
    });
  }

  private saveProductsAndWithholdings(
    invoice: InvoiceInterface,
    onlyForPreview: boolean = false,
  ): void {
    const orgId = this.data.organizationId;

    const productCalls: Observable<ProductServiceInterface>[] =
      this.productLines.map((line) =>
        this.productServiceApi.createProductService(invoice.id, orgId, {
          invoice_id: invoice.id,
          name: line.name,
          quantity: line.quantity,
          gross_amount: line.gross_amount,
          discount: line.discount,
          discount_amount: this.roundToTwo(
            line.gross_amount * line.quantity * (line.discount / 100),
          ),
          amount: line.lineTotal,
          iva_percentage: 0,
          iva_amount: 0,
          final_amount: line.lineTotal,
          units: line.units,
          description: line.description,
        }),
      );

    const withholdings: Partial<TaxWithholdingInterface>[] = [];
    if (this.hasRivalsa) {
      withholdings.push({
        kind: 'RIVALSA',
        rate_percentage: this.rivalsaRate,
        total_percentage: this.rivalsaTotalPercentage,
        rate_amount: this.rivalsaAmount,
      });
    }
    if (this.hasCassa) {
      withholdings.push({
        kind: 'CASSA',
        rate_percentage: this.cassaRate,
        total_percentage: this.cassaTotalPercentage,
        rate_amount: this.cassaAmount,
      });
    }
    if (this.hasRitenutaAcconto) {
      withholdings.push({
        kind: 'RITENUTA_ACCONTO',
        rate_percentage: this.ritenutaAccontoRate,
        total_percentage: this.ritenutaAccontoTotalPercentage,
        rate_amount: this.ritenutaAccontoAmount,
      });
    }
    if (this.hasRitenuta) {
      withholdings.push({
        kind: 'RITENUTA',
        rate_percentage: this.ritenutaRate,
        total_percentage: this.ritenutaTotalPercentage,
        rate_amount: this.ritenutaAmount,
      });
    }

    const withholdingCalls: Observable<TaxWithholdingInterface>[] =
      withholdings.map((w) =>
        this.taxWithholdingApi.createTaxWithholding(invoice.id, orgId, {
          ...w,
          invoice_id: invoice.id,
        }),
      );

    const allCalls: Observable<any>[] = [...productCalls, ...withholdingCalls];

    forkJoin(allCalls.length > 0 ? allCalls : [of(null)]).subscribe({
      next: () => {
        if (onlyForPreview) {
          // Modalità anteprima: mostra il PDF e lascia il bottom sheet aperto
          // per permettere all'utente di continuare a modificare e poi inviare
          this.isGeneratingPreview = false;
          this.showPdfPreview(invoice.id!);
        } else {
          this.isSubmitting = false;
          // Stesso flusso di new-invoice: dialog di conferma con download/invio email
          this.openDownloadConfirmationDialog(invoice);
          this.bottomSheetRef.dismiss({ reload: true });
        }
      },
      error: (err) => {
        console.error('Error saving products/withholdings:', err);
        if (onlyForPreview) {
          this.isGeneratingPreview = false;
          this.showError(
            'Anteprima creata, ma errore nel salvataggio delle righe.',
          );
          this.showPdfPreview(invoice.id!);
        } else {
          this.isSubmitting = false;
          this.showError(
            'Fattura creata, ma errore nel salvataggio delle righe.',
          );
          this.bottomSheetRef.dismiss({ reload: true });
        }
      },
    });
  }

  // ─── Anteprima PDF (stesso flusso di new-invoice.showPdf/openPdfDialog) ────

  async showPdfPreview(invoiceId: number): Promise<void> {
    try {
      const blobUrl = await this.invoiceApi.showPdfInDialog(invoiceId);
      const dialogRef = this.dialog.open(PdfViewerDialogComponent, {
        width: '90vw',
        maxWidth: '1200px',
        height: '90vh',
        data: {
          pdfUrl: blobUrl,
          invoiceId: invoiceId,
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        window.URL.revokeObjectURL(blobUrl);
      });
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      this.showError('Errore nel caricamento dell’anteprima PDF.');
    } finally {
      this.cdr.detectChanges();
    }
  }

  close(): void {
    this.bottomSheetRef.dismiss();
  }

  // ─── Espandi in fattura completa ────────────────────────────────────────
  // Trasporta tutto quanto già compilato nel bottom sheet verso new-invoice,
  // dove l'utente ritrova il lavoro fatto e può sbloccare i campi extra
  // (ATECO, IBAN, PA, fattura sanitaria, CIG/CUP, ecc.). Nessuna validazione
  // bloccante: l'espansione è un punto di partenza, non un invio.
  expandToFullInvoice(): void {
    const withholdings: { kind: KindCode; rate_percentage: number }[] = [];
    if (this.hasRivalsa) {
      withholdings.push({ kind: 'RIVALSA', rate_percentage: this.rivalsaRate });
    }
    if (this.hasCassa) {
      withholdings.push({ kind: 'CASSA', rate_percentage: this.cassaRate });
    }
    if (this.hasRitenutaAcconto) {
      withholdings.push({
        kind: 'RITENUTA_ACCONTO',
        rate_percentage: this.ritenutaAccontoRate,
      });
    }
    if (this.hasRitenuta) {
      withholdings.push({
        kind: 'RITENUTA',
        rate_percentage: this.ritenutaRate,
      });
    }

    const draft: QuickInvoiceDraft = {
      organizationId: this.data.organizationId,
      company: this.selectedCompany,
      progressiveNumber: this.progressiveNumber,
      enumeration: this.enumeration,
      description: this.mainForm.value.description ?? '',
      productLines: this.productLines.map((line) => ({
        name: line.name,
        quantity: line.quantity,
        units: line.units,
        gross_amount: line.gross_amount,
        discount: line.discount,
        description: line.description,
      })),
      withholdings,
      revenueStampOption: this.revenueStampOption,
      cassaPensionFundType: this.cassaPensionFundType,
      isPaperInvoice: this.isPaperInvoice,
      isHealthcareInvoice: this.isHealthcareInvoice,
      atecoCodeId: this.selectedAtecoCodeId,
      paymentMethod: this.paymentMethod,
      emissionDate: this.emissionDate,
      paymentDays: this.paymentDays,
      addresseeCode: this.addresseeCode,
      iban: this.iban,
    };

    this.quickInvoiceTransferService.setDraft(draft);
    this.bottomSheetRef.dismiss();
    this.router
      .navigate([`/${this.data.organizationId}/new_invoice`])
      .then(() => {
        this.showSuccess('Dati trasferiti nella fattura completa.');
      });
  }

  // ─── Post-invio: dialog di conferma download/invio (stesso flusso di new-invoice) ───

  openDownloadConfirmationDialog(invoice: InvoiceInterface): void {
    const downloadDialogRef = this.dialog.open(
      DownloadConfirmationDialogComponent,
      {
        width: '90vw',
        maxWidth: '500px',
        data: {
          title: 'Fattura creata con successo!',
          documentType: 'Il documento',
          invoiceId: invoice.id!,
          invoiceName: invoice.invoice_name,
          companyName: this.selectedCompany?.company_name,
          text: 'è stato creato correttamente.',
        },
      },
    );

    downloadDialogRef.afterClosed().subscribe(async (result) => {
      if (result && result.download) {
        this.downloadPdf(invoice.id!);
      } else if (result && result.sendEmail) {
        await this.sendInvoiceEmail(invoice.id!, invoice);
      }
    });
  }

  downloadPdf(invoiceId: number): void {
    try {
      this.invoiceApi.downloadInvoicePdf(invoiceId);
      this.showSuccess(
        'La fattura è stata scaricata sul dispositivo con successo!',
      );
    } catch (error) {
      this.showError('Errore download PDF.');
    }
  }

  async sendInvoiceEmail(
    invoiceId: number,
    invoice: InvoiceInterface,
  ): Promise<void> {
    const orgId = this.data.organizationId;

    const dialogRef = this.dialog.open(ConfirmEmailSendDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      height: '400px',
      data: {
        email_address: this.selectedCompany?.email_address,
        company_id: this.selectedCompany?.id,
        organization_id: orgId,
      },
    });
    const confirmed = await dialogRef.afterClosed().toPromise();

    if (!confirmed) {
      return;
    }

    const loadingDialogRef = this.dialog.open(LoadingDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      height: '300px',
      disableClose: true,
    });

    try {
      await this.invoiceApi.sendEmailWithPDF(orgId, invoiceId, invoice);
      loadingDialogRef.close();
      this.showSuccess('La fattura è stata inviata al cliente con successo!');
    } catch (error) {
      loadingDialogRef.close();
      this.showError('Errore invio email con PDF.');
    }
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 4000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}
